/**
 * Environment Limits Configuration
 *
 * Defines limits for environment-based org management system.
 * Professional Plan users get environments (1 production + sandboxes).
 * Standard Plan users get standalone orgs only.
 */

export const ENVIRONMENT_LIMITS = {
  /**
   * Maximum number of orgs allowed per environment
   * Can be adjusted as needed - affects all plans with environment support
   */
  MAX_ORGS_PER_ENVIRONMENT: parseInt(import.meta.env.VITE_MAX_ORGS_PER_ENVIRONMENT || '5'),

  /**
   * Standard Plan: Maximum total orgs (not environments)
   * Standard users cannot leverage environment grouping
   */
  STARTER_ORG_LIMIT: 1,

  /**
   * Professional Plan: Base number of environments included
   * Additional environments can be purchased via Stripe add-on
   */
  PROFESSIONAL_BASE_ENVIRONMENTS: 1,

  /**
   * Free Plan: Maximum total orgs
   */
  FREE_ORG_LIMIT: 1,
} as const;

/**
 * Plan types that support environment grouping
 */
export const ENVIRONMENT_ENABLED_PLANS = ['professional', 'pro', 'enterprise'] as const;

/**
 * Check if a plan supports environment-based org management
 */
export function isEnvironmentPlan(plan: string | null | undefined): boolean {
  if (!plan) return false;
  return ENVIRONMENT_ENABLED_PLANS.includes(plan.toLowerCase() as any);
}

/**
 * Get the org limit for non-environment plans (Standard, Free)
 */
export function getStandaloneOrgLimit(plan: string | null | undefined): number {
  const planKey = plan?.toLowerCase();

  if (planKey === 'standard' || planKey === 'starter') {  // Support both for legacy
    return ENVIRONMENT_LIMITS.STARTER_ORG_LIMIT;
  }

  return ENVIRONMENT_LIMITS.FREE_ORG_LIMIT;
}

/**
 * Get base environment limit for environment-enabled plans
 */
export function getBaseEnvironmentLimit(plan: string | null | undefined): number {
  if (!isEnvironmentPlan(plan)) {
    return 0; // Non-environment plans don't get environments
  }

  return ENVIRONMENT_LIMITS.PROFESSIONAL_BASE_ENVIRONMENTS;
}

/**
 * Get maximum orgs per environment
 * Future: Could vary by plan or be customizable per account
 */
export function getMaxOrgsPerEnvironment(): number {
  return ENVIRONMENT_LIMITS.MAX_ORGS_PER_ENVIRONMENT;
}

/**
 * Display messages for limit scenarios
 */
export const LIMIT_MESSAGES = {
  STARTER_ORG_LIMIT_REACHED: 'You\'ve reached your org limit. Upgrade to Professional to connect multiple environments with sandboxes.',
  ENVIRONMENT_LIMIT_REACHED: 'You\'ve reached your environment limit. Purchase additional environments to connect more Salesforce orgs.',
  ENVIRONMENT_ORG_LIMIT_REACHED: `This environment has reached the maximum of ${ENVIRONMENT_LIMITS.MAX_ORGS_PER_ENVIRONMENT} orgs. Connect to a different environment or contact support for Enterprise pricing.`,
  UPGRADE_TO_PROFESSIONAL: 'Upgrade to Professional to connect sandboxes and manage multiple environments.',
} as const;
